import React, { useState, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  ArrowLeft, Phone, Mail, Calendar, Home, FileText, Archive, ChevronRight,
  DollarSign, Clock, Camera, AlertTriangle, CheckCircle, Receipt, MessageSquare,
  Send, X, Loader, Upload, Image, Trash2, Edit3, GitMerge, Save, MapPin,
  Play, Flag, Navigation, Users, ChevronDown, Download, Lock, RefreshCw,
  ClipboardList, Plus
} from 'lucide-react';
import { formatFullName, formatPhone, formatAddress } from '../utils/helpers';
import { clientTypes, frequencyOptions } from '../utils/sampleData';
import { format, parseISO, differenceInDays } from 'date-fns';
import { sendWelcomeEmail, sendPinResetEmail } from '../utils/emailApi';
import { uploadPropertyPhoto, deletePropertyPhoto, deleteJobPhoto } from '../utils/photoUpload';
import { ref as storageRef, getBlob } from 'firebase/storage';
import { storage } from '../firebase';
import { useGoogleMaps } from '../context/GoogleMapsContext';
import ClientDocuments from '../components/ClientDocuments';
import { CHECKLIST_FREQUENCY_OPTIONS } from '../utils/checklistFrequency';
import './ClientDetail.css';

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, getClientById, getClientProperties, getClientEstimates, updateEstimate, updateClient, updateProperty, deleteProperty, mergeProperties, deleteSeriesJobs, deleteJob, updateJob, getJobInvoice, logClientCommunication, getClientDocuments, deleteClient, verifyMasterPassword, hasMasterPassword } = useApp();
  const [showArchived, setShowArchived] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [sendingWelcome, setSendingWelcome] = useState(false);
  const [showPinResetModal, setShowPinResetModal] = useState(false);
  const [sendingPinReset, setSendingPinReset] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // propertyId being uploaded
  const [deletingPhoto, setDeletingPhoto] = useState(null); // propertyId being deleted
  const photoInputRef = useRef(null);

  const { isLoaded: mapsLoaded } = useGoogleMaps();

  // Property editing state
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [editPropertyData, setEditPropertyData] = useState({});

  // Property merge state
  const [showMergePropertyModal, setShowMergePropertyModal] = useState(false);
  const [mergePropertyPrimary, setMergePropertyPrimary] = useState(null);
  const [mergePropertySecondary, setMergePropertySecondary] = useState(null);
  const [mergingProperties, setMergingProperties] = useState(false);

  // Series delete state
  const [showDeleteSeriesModal, setShowDeleteSeriesModal] = useState(null); // { recurringScheduleId, seriesName, jobCount }
  const [deletingSeries, setDeletingSeries] = useState(false);
  const [deleteSeriesScope, setDeleteSeriesScope] = useState('all_scheduled');

  // Job log expand state
  const [expandedJobId, setExpandedJobId] = useState(null);

  // Job edit/delete state
  const [editingJob, setEditingJob] = useState(null);
  const [editJobData, setEditJobData] = useState({});
  const [savingJob, setSavingJob] = useState(false);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [confirmDeleteJob, setConfirmDeleteJob] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Photo delete state
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);

  // PDF download state
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Photo zip download state
  const [downloadingPhotos, setDownloadingPhotos] = useState(false);

  // Custom checklist state
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [newChecklistFrequency, setNewChecklistFrequency] = useState('daily');
  const [savingChecklist, setSavingChecklist] = useState(false);

  // Delete client state
  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [masterPassword, setMasterPasswordInput] = useState('');
  const [deleteClientError, setDeleteClientError] = useState('');
  const [deletingClient, setDeletingClient] = useState(false);
  
  // Safe date parsers (handles Firestore timestamps + strings)
  const safeParse = (val) => {
    if (!val) return null;
    if (val.toDate) return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === 'string') { try { return parseISO(val); } catch { return null; } }
    return null;
  };
  const safeFmt = (val, fmt) => {
    const d = safeParse(val);
    return d ? format(d, fmt) : '—';
  };

  const client = getClientById(id);
  const properties = getClientProperties(id);
  const allEstimates = getClientEstimates(id);
  
  // Get all jobs for this client
  const clientJobs = useMemo(() => {
    return state.jobs.filter(j => j.clientId === id).sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
  }, [state.jobs, id]);

  // Jobs eligible for bulk delete (everything except completed)
  const deletableJobs = useMemo(() => {
    return clientJobs.filter(j => j.status !== 'completed');
  }, [clientJobs]);

  // Get all invoices for this client
  const clientInvoices = useMemo(() => {
    return state.invoices.filter(i => i.clientId === id).sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [state.invoices, id]);

  // Calculate account health metrics
  const accountHealth = useMemo(() => {
    const completedJobs = clientJobs.filter(j => j.status === 'completed');
    
    // Recalculate labor per job same as Reports page
    const jobsWithLabor = completedJobs.map(job => {
      const actualHours = job.actual_duration_min 
        ? job.actual_duration_min / 60 
        : (job.actualDuration ? job.actualDuration / 60 : job.duration || 0);
      let laborCost = 0;

      const allWorkers = [...(job.team || []), ...(job.helpers || [])];
      if (allWorkers.length > 0) {
        allWorkers.forEach(empId => {
          const emp = state.employees.find(e => e.id === empId);
          const hourlyRate = emp?.hourlyRate || 15;
          laborCost += actualHours * hourlyRate;
        });
      } else if (actualHours > 0) {
        laborCost = actualHours * 15 * 2;
      }

      const revenue = job.price || 0;
      const laborPercent = revenue > 0 ? Math.round((laborCost / revenue) * 100) : 0;
      const profit = revenue - laborCost;

      return { ...job, calcLaborCost: laborCost, calcLaborPercent: laborPercent, calcProfit: profit, actualHours };
    });

    // Revenue & Profitability
    const totalRevenue = completedJobs.reduce((sum, j) => sum + (j.price || 0), 0);
    const totalLaborCost = jobsWithLabor.reduce((sum, j) => sum + j.calcLaborCost, 0);
    const totalProfit = totalRevenue - totalLaborCost;
    const overallLaborPercent = totalRevenue > 0 ? Math.round((totalLaborCost / totalRevenue) * 100) : null;
    
    // Invoice health
    const paidInvoices = clientInvoices.filter(i => i.status === 'paid');
    const unpaidInvoices = clientInvoices.filter(i => i.status === 'sent' || i.status === 'overdue');
    const totalPaid = paidInvoices.reduce((sum, i) => sum + (i.total || 0), 0);
    const totalOutstanding = unpaidInvoices.reduce((sum, i) => sum + (i.total || 0), 0);
    
    // Calculate avg days to pay
    const paidWithDates = paidInvoices.filter(i => i.paidAt && i.sentAt);
    const avgDaysToPay = paidWithDates.length > 0
      ? Math.round(paidWithDates.reduce((sum, i) => {
          return sum + differenceInDays(new Date(i.paidAt), new Date(i.sentAt));
        }, 0) / paidWithDates.length)
      : null;
    
    // Job stats
    const totalJobs = clientJobs.length;
    const scheduledJobs = clientJobs.filter(j => j.status === 'scheduled').length;
    const cancelledJobs = clientJobs.filter(j => j.status === 'cancelled').length;
    
    // Time tracking
    const jobsWithTime = completedJobs.filter(j => j.actual_duration_min && j.duration);
    const avgActualHours = jobsWithTime.length > 0
      ? (jobsWithTime.reduce((sum, j) => sum + (j.actual_duration_min || 0), 0) / jobsWithTime.length / 60).toFixed(1)
      : null;
    const avgExpectedHours = jobsWithTime.length > 0
      ? (jobsWithTime.reduce((sum, j) => sum + (j.duration || 0), 0) / jobsWithTime.length).toFixed(1)
      : null;
    
    // Avg duration in minutes
    const avgDurationMin = completedJobs.length > 0
      ? Math.round(completedJobs.reduce((sum, j) => sum + (j.actual_duration_min || 0), 0) / completedJobs.length)
      : 0;
    // Photos count
    const totalPhotos = completedJobs.reduce((sum, j) => sum + (j.photos?.length || 0), 0);
    
    // Damage reports
    const damageReports = completedJobs.reduce((sum, j) => sum + (j.damageReports?.length || 0), 0);
    
    // Health score (0-100)
    let healthScore = 70; // Base score
    if (overallLaborPercent != null) {
      if (overallLaborPercent <= 40) healthScore += 15;
      else if (overallLaborPercent <= 50) healthScore += 5;
      else healthScore -= 10;
    }
    if (totalOutstanding === 0) healthScore += 10;
    else if (totalOutstanding > totalPaid * 0.5) healthScore -= 15;
    if (cancelledJobs > completedJobs.length * 0.1) healthScore -= 10;
    healthScore = Math.max(0, Math.min(100, healthScore));
    
    return {
      totalRevenue,
      totalLaborCost,
      totalProfit,
      overallLaborPercent,
      jobsWithLabor,
      totalPaid,
      totalOutstanding,
      avgDaysToPay,
      totalJobs,
      completedJobs: completedJobs.length,
      scheduledJobs,
      cancelledJobs,
      avgActualHours,
      avgExpectedHours,
      avgDurationMin,
      totalPhotos,
      damageReports,
      healthScore
    };
  }, [clientJobs, clientInvoices, state.employees]);

  // Separate active vs archived estimates
  const activeEstimates = allEstimates.filter(e => e.status !== 'archived');
  const archivedEstimates = allEstimates.filter(e => e.status === 'archived');
  const displayEstimates = showArchived ? archivedEstimates : activeEstimates;

  const handleArchiveEstimate = async (estimateId, e) => {
    e.stopPropagation();
    if (window.confirm('Archive this estimate? It will be hidden from the main view.')) {
      await updateEstimate(estimateId, { status: 'archived', archivedAt: new Date().toISOString() });
    }
  };

  const handleRestoreEstimate = async (estimateId, e) => {
    e.stopPropagation();
    await updateEstimate(estimateId, { status: 'draft', archivedAt: null });
  };

  // Property photo upload handler
  // ===== PROPERTY EDITING =====
  const startEditingProperty = (prop) => {
    setEditingPropertyId(prop.id);
    setEditPropertyData({
      addressLine1: prop.addressLine1 || '',
      city: prop.city || '',
      state: prop.state || '',
      zip: prop.zip || '',
      sqft: prop.sqft || '',
      bedrooms: prop.bedrooms || '',
      bathrooms: prop.bathrooms || '',
      pets: prop.pets || '',
      accessInfo: prop.accessInfo || ''
    });
  };

  const savePropertyEdit = async () => {
    if (!editingPropertyId) return;
    try {
      const updates = { ...editPropertyData };
      if (updates.sqft) updates.sqft = Number(updates.sqft);
      if (updates.bedrooms) updates.bedrooms = Number(updates.bedrooms);
      if (updates.bathrooms) updates.bathrooms = Number(updates.bathrooms);

      // Auto-geocode the address to store coordinates for proximity search
      if (mapsLoaded && window.google?.maps?.Geocoder && updates.addressLine1) {
        const fullAddr = `${updates.addressLine1}, ${updates.city || 'Charlottesville'}, ${updates.state || 'VA'} ${updates.zip || ''}`.trim();
        try {
          const geocoder = new window.google.maps.Geocoder();
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: fullAddr }, (results, status) => {
              if (status === 'OK' && results[0]) resolve(results[0]);
              else reject(new Error(status));
            });
          });
          updates.coordinates = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
          };
        } catch (geoErr) {
          console.warn('Could not geocode property address:', geoErr);
          // Continue saving without coordinates - don't block the save
        }
      }

      await updateProperty(editingPropertyId, updates);

      // Also update any scheduled jobs that reference this property
      const propertyJobs = state.jobs.filter(j => j.propertyId === editingPropertyId && j.status === 'scheduled');
      for (const job of propertyJobs) {
        const fullAddr = `${updates.addressLine1}, ${updates.city}, ${updates.state} ${updates.zip}`;
        // We just update via the app context's updateJob equivalent
        // Actually jobs use address/fullAddress fields
      }

      setEditingPropertyId(null);
      setEditPropertyData({});
    } catch (error) {
      console.error('Failed to update property:', error);
      alert('Failed to update property. Please try again.');
    }
  };

  // ===== PROPERTY MERGING =====
  const handleMergeProperties = async () => {
    if (!mergePropertyPrimary || !mergePropertySecondary) return;
    setMergingProperties(true);
    try {
      await mergeProperties(mergePropertyPrimary, mergePropertySecondary);
      setShowMergePropertyModal(false);
      setMergePropertyPrimary(null);
      setMergePropertySecondary(null);
      alert('✅ Properties merged successfully!');
    } catch (error) {
      console.error('Failed to merge properties:', error);
      alert('Failed to merge properties. Please try again.');
    }
    setMergingProperties(false);
  };

  // ===== DELETE SERIES =====
  const handleDeleteSeries = async () => {
    if (!showDeleteSeriesModal) return;
    setDeletingSeries(true);
    try {
      const result = await deleteSeriesJobs(showDeleteSeriesModal.recurringScheduleId, {
        scope: deleteSeriesScope
      });
      setShowDeleteSeriesModal(null);
      setDeleteSeriesScope('all_scheduled');
      alert(`✅ Deleted ${result.deleted} jobs from the series.`);
    } catch (error) {
      console.error('Failed to delete series:', error);
      alert('Failed to delete series. Please try again.');
    }
    setDeletingSeries(false);
  };

  // Group jobs by recurring series for the series management view
  const jobSeries = useMemo(() => {
    const seriesMap = {};
    clientJobs.forEach(job => {
      if (job.recurringScheduleId) {
        if (!seriesMap[job.recurringScheduleId]) {
          seriesMap[job.recurringScheduleId] = {
            recurringScheduleId: job.recurringScheduleId,
            serviceType: job.serviceType,
            address: job.address || job.fullAddress || '',
            clientName: job.clientName,
            jobs: [],
            scheduledCount: 0,
            completedCount: 0,
            cancelledCount: 0
          };
        }
        seriesMap[job.recurringScheduleId].jobs.push(job);
        if (job.status === 'scheduled') seriesMap[job.recurringScheduleId].scheduledCount++;
        if (job.status === 'completed') seriesMap[job.recurringScheduleId].completedCount++;
        if (job.status === 'cancelled') seriesMap[job.recurringScheduleId].cancelledCount++;
      }
    });
    return Object.values(seriesMap);
  }, [clientJobs]);

  const handlePropertyPhotoUpload = async (propertyId, file) => {
    if (!file) return;
    setUploadingPhoto(propertyId);
    try {
      const photoUrl = await uploadPropertyPhoto(file, propertyId);
      await updateProperty(propertyId, { photoUrl });
    } catch (error) {
      console.error('Failed to upload property photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePropertyPhotoDelete = async (propertyId, photoUrl) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    setDeletingPhoto(propertyId);
    try {
      await deletePropertyPhoto(photoUrl);
      await updateProperty(propertyId, { photoUrl: '' });
    } catch (error) {
      console.error('Failed to delete property photo:', error);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setDeletingPhoto(null);
    }
  };

  if (!client) {
    return (
      <div className="detail-page">
        <div className="not-found">
          <h2>Client not found</h2>
          <Link to="/admin/clients" className="btn btn-secondary">
            <ArrowLeft size={18} />
            Back to Clients
          </Link>
        </div>
      </div>
    );
  }

  const typeConfig = clientTypes[client.clientType];
  const frequency = frequencyOptions.find(f => f.value === client.frequency);

  const getStatusColor = (status) => {
    const colors = {
      draft: '#6b7280',
      sent: '#d97706',
      approved: '#16a34a',
      declined: '#dc2626',
      converted: '#2563eb',
      archived: '#9ca3af'
    };
    return colors[status] || '#6b7280';
  };

  const getJobStatusConfig = (status) => {
    const configs = {
      scheduled: { color: '#3b82f6', bg: '#eff6ff', label: 'Scheduled' },
      in_progress: { color: '#f59e0b', bg: '#fffbeb', label: 'In Progress' },
      completed: { color: '#16a34a', bg: '#f0fdf4', label: 'Completed' },
      cancelled: { color: '#dc2626', bg: '#fef2f2', label: 'Cancelled' }
    };
    return configs[status] || { color: '#6b7280', bg: '#f3f4f6', label: status };
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return '#16a34a';
    if (score >= 60) return '#f59e0b';
    return '#dc2626';
  };

  // Job edit handlers
  const openEditJob = (job) => {
    setEditJobData({
      date: job.date || '',
      scheduledTime: job.scheduledTime || '',
      price: job.price || 0,
      serviceType: job.serviceType || '',
      duration: job.duration || 2.5,
      notes: job.notes || '',
      status: job.status || 'scheduled',
      address: job.address || ''
    });
    setEditingJob(job);
  };

  const handleSaveJob = async () => {
    if (!editingJob) return;
    setSavingJob(true);
    try {
      await updateJob(editingJob.id, {
        date: editJobData.date,
        scheduledTime: editJobData.scheduledTime,
        price: parseFloat(editJobData.price) || 0,
        serviceType: editJobData.serviceType,
        duration: parseFloat(editJobData.duration) || 2.5,
        notes: editJobData.notes,
        status: editJobData.status,
        address: editJobData.address
      });
      setEditingJob(null);
      setEditJobData({});
    } catch (err) {
      console.error('Error updating job:', err);
      alert('Failed to update job. Please try again.');
    } finally {
      setSavingJob(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    setDeletingJobId(jobId);
    try {
      await deleteJob(jobId);
      setConfirmDeleteJob(null);
      if (expandedJobId === jobId) setExpandedJobId(null);
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('Failed to delete job. Please try again.');
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleBulkDeleteJobs = async () => {
    setBulkDeleting(true);
    try {
      for (const job of deletableJobs) {
        await deleteJob(job.id);
      }
      setShowBulkDeleteModal(false);
      setExpandedJobId(null);
    } catch (err) {
      console.error('Error bulk deleting jobs:', err);
      alert('Failed to delete some jobs. Please try again.');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Photo delete handler (manager only)
  const handleDeletePhoto = async (jobId, photo, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    const photoKey = photo.id || photo.path || photo.url;
    setDeletingPhotoId(photoKey);
    try {
      await deleteJobPhoto(jobId, photo);
    } catch (err) {
      console.error('Error deleting photo:', err);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setDeletingPhotoId(null);
    }
  };

  // Delete client handlers
  const handleDeleteClientClick = async () => {
    const hasPassword = await hasMasterPassword();
    if (!hasPassword) {
      setDeleteClientError('Master password has not been set. Please set it in Settings first.');
      setShowDeleteClientModal(true);
      return;
    }
    setMasterPasswordInput('');
    setDeleteClientError('');
    setShowDeleteClientModal(true);
  };

  const handleConfirmDeleteClient = async () => {
    if (!masterPassword.trim()) {
      setDeleteClientError('Please enter the master password.');
      return;
    }
    setDeletingClient(true);
    setDeleteClientError('');
    const result = await verifyMasterPassword(masterPassword);
    if (!result.valid) {
      setDeleteClientError(result.notSet ? 'Master password has not been set. Please set it in Settings first.' : 'Incorrect master password.');
      setDeletingClient(false);
      return;
    }
    await deleteClient(id);
    setShowDeleteClientModal(false);
    setDeletingClient(false);
    navigate('/admin/clients');
  };

  // PDF report download
  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;
      let y = 20;

      const checkNewPage = (needed = 30) => {
        if (y + needed > pdf.internal.pageSize.getHeight() - 20) {
          pdf.addPage();
          y = 20;
        }
      };

      // ===== HEADER =====
      pdf.setFontSize(22);
      pdf.setFont(undefined, 'bold');
      pdf.text('Two Sisters Cleaning Co.', margin, y);
      y += 8;
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100);
      pdf.text(`Client Report — Generated ${format(new Date(), 'MMMM d, yyyy')}`, margin, y);
      pdf.setTextColor(0);
      y += 12;

      // ===== CLIENT INFO =====
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text(formatFullName(client), margin, y);
      y += 7;
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      if (client.email) { pdf.text(`Email: ${client.email}`, margin, y); y += 5; }
      if (client.phone) { pdf.text(`Phone: ${formatPhone(client.phone)}`, margin, y); y += 5; }
      const freq = frequencyOptions.find(f => f.value === client.frequency);
      if (freq) { pdf.text(`Service Frequency: ${freq.label}`, margin, y); y += 5; }
      pdf.text(`Client Type: ${clientTypes[client.clientType]?.label || client.clientType || 'N/A'}`, margin, y);
      y += 5;
      pdf.text(`Status: ${client.status || 'active'}`, margin, y);
      y += 10;

      // ===== PROPERTIES =====
      if (properties.length > 0) {
        checkNewPage(20);
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('Properties', margin, y);
        y += 7;
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        properties.forEach(prop => {
          checkNewPage(15);
          pdf.text(`• ${formatAddress(prop)}`, margin, y);
          y += 5;
          const details = [];
          if (prop.sqft) details.push(`${prop.sqft} sqft`);
          if (prop.bedrooms) details.push(`${prop.bedrooms} bed`);
          if (prop.bathrooms) details.push(`${prop.bathrooms} bath`);
          if (details.length) { pdf.text(`  ${details.join(' | ')}`, margin, y); y += 5; }
        });
        y += 5;
      }

      // ===== FINANCIAL SUMMARY =====
      checkNewPage(40);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Financial Summary', margin, y);
      y += 7;
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Total Revenue: $${accountHealth.totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2})}`, margin, y); y += 5;
      pdf.text(`Total Labor Cost: $${accountHealth.totalLaborCost.toLocaleString(undefined, {minimumFractionDigits: 2})}${accountHealth.overallLaborPercent != null ? ` (${accountHealth.overallLaborPercent}%)` : ''}`, margin, y); y += 5;
      pdf.text(`Net Profit: $${accountHealth.totalProfit.toLocaleString(undefined, {minimumFractionDigits: 2})}`, margin, y); y += 5;
      pdf.text(`Outstanding: $${accountHealth.totalOutstanding.toLocaleString(undefined, {minimumFractionDigits: 2})}`, margin, y); y += 5;
      if (accountHealth.avgDaysToPay != null) {
        pdf.text(`Avg Days to Pay: ${accountHealth.avgDaysToPay}`, margin, y); y += 5;
      }
      y += 5;

      // ===== JOB HISTORY =====
      checkNewPage(20);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text(`Job History (${clientJobs.length} jobs)`, margin, y);
      y += 7;
      pdf.setFontSize(9);
      pdf.setFont(undefined, 'normal');

      // Table header
      checkNewPage(12);
      pdf.setFont(undefined, 'bold');
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, y - 4, contentWidth, 7, 'F');
      pdf.text('Date', margin + 2, y);
      pdf.text('Service', margin + 35, y);
      pdf.text('Status', margin + 72, y);
      pdf.text('Price', margin + 100, y);
      pdf.text('Duration', margin + 125, y);
      pdf.text('Photos', margin + 155, y);
      y += 7;
      pdf.setFont(undefined, 'normal');

      clientJobs.forEach(job => {
        checkNewPage(8);
        const jobDate = safeFmt(job.date, 'MM/dd/yy');
        const svc = (job.serviceType || '').replace(/_/g, ' ').slice(0, 18);
        const status = job.status || '';
        const price = `$${(job.price || 0).toFixed(2)}`;
        const dur = job.actual_duration_min ? `${Math.floor(job.actual_duration_min / 60)}h ${job.actual_duration_min % 60}m` : '—';
        const photoCount = (job.photos || []).length.toString();

        pdf.text(jobDate, margin + 2, y);
        pdf.text(svc, margin + 35, y);
        pdf.text(status, margin + 72, y);
        pdf.text(price, margin + 100, y);
        pdf.text(dur, margin + 125, y);
        pdf.text(photoCount, margin + 155, y);
        y += 6;
      });
      y += 5;

      // ===== INVOICES =====
      if (clientInvoices.length > 0) {
        checkNewPage(20);
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text(`Invoices (${clientInvoices.length})`, margin, y);
        y += 7;
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'normal');

        // Table header
        checkNewPage(12);
        pdf.setFont(undefined, 'bold');
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, y - 4, contentWidth, 7, 'F');
        pdf.text('Invoice #', margin + 2, y);
        pdf.text('Date', margin + 35, y);
        pdf.text('Amount', margin + 72, y);
        pdf.text('Tips', margin + 100, y);
        pdf.text('Status', margin + 125, y);
        pdf.text('Paid', margin + 155, y);
        y += 7;
        pdf.setFont(undefined, 'normal');

        clientInvoices.forEach(inv => {
          checkNewPage(8);
          pdf.text(inv.invoiceNumber || '—', margin + 2, y);
          pdf.text(inv.createdAt ? format(parseISO(inv.createdAt), 'MM/dd/yy') : '—', margin + 35, y);
          pdf.text(`$${(inv.total || 0).toFixed(2)}`, margin + 72, y);
          pdf.text(inv.tipAmount > 0 ? `$${inv.tipAmount.toFixed(2)}` : '—', margin + 100, y);
          pdf.text(inv.status || '—', margin + 125, y);
          pdf.text(inv.paidAt ? format(parseISO(inv.paidAt), 'MM/dd/yy') : '—', margin + 155, y);
          y += 6;
        });
        y += 5;
      }

      // ===== PHOTOS =====
      const jobsWithPhotos = clientJobs.filter(j => j.photos && j.photos.length > 0);
      if (jobsWithPhotos.length > 0) {
        checkNewPage(20);
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('Job Photos', margin, y);
        y += 7;

        for (const job of jobsWithPhotos) {
          checkNewPage(15);
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'bold');
          pdf.text(`${safeFmt(job.date, 'MMM d, yyyy')} — ${(job.serviceType || '').replace(/_/g, ' ')}`, margin, y);
          y += 6;
          pdf.setFont(undefined, 'normal');

          const photoSize = 40;
          const gap = 5;
          const cols = Math.floor(contentWidth / (photoSize + gap));
          let col = 0;

          for (const photo of job.photos) {
            try {
              checkNewPage(photoSize + 12);
              // Fetch image and convert to base64
              let blob;
              if (photo.path) {
                const photoRef = storageRef(storage, photo.path);
                blob = await getBlob(photoRef);
              } else {
                const imgUrl = photo.url || photo;
                const response = await fetch(imgUrl);
                blob = await response.blob();
              }
              const base64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });

              const x = margin + col * (photoSize + gap);
              pdf.addImage(base64, 'JPEG', x, y, photoSize, photoSize);
              if (photo.type) {
                pdf.setFontSize(7);
                pdf.text(photo.type, x, y + photoSize + 3);
                pdf.setFontSize(10);
              }
              col++;
              if (col >= cols) {
                col = 0;
                y += photoSize + 8;
              }
            } catch (imgErr) {
              // Skip photos that fail to load
              console.warn('Could not load photo for PDF:', imgErr);
            }
          }
          if (col > 0) y += photoSize + 8;
          y += 3;
        }
      }

      // ===== DAMAGE REPORTS =====
      const jobsWithDamage = clientJobs.filter(j => j.damageReports && j.damageReports.length > 0);
      if (jobsWithDamage.length > 0) {
        checkNewPage(20);
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text('Damage Reports', margin, y);
        y += 7;
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');

        jobsWithDamage.forEach(job => {
          job.damageReports.forEach(report => {
            checkNewPage(20);
            pdf.setFont(undefined, 'bold');
            pdf.text(`${safeFmt(job.date, 'MMM d, yyyy')} — ${report.type === 'existing' ? 'Pre-existing' : 'New'} Damage`, margin, y);
            y += 5;
            pdf.setFont(undefined, 'normal');
            if (report.description) {
              const lines = pdf.splitTextToSize(report.description, contentWidth);
              lines.forEach(line => { checkNewPage(6); pdf.text(line, margin, y); y += 5; });
            }
            y += 3;
          });
        });
      }

      // Footer on last page
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 20, pdf.internal.pageSize.getHeight() - 10);
        pdf.text('Two Sisters Cleaning Co. — Confidential', margin, pdf.internal.pageSize.getHeight() - 10);
        pdf.setTextColor(0);
      }

      const clientName = formatFullName(client).replace(/\s+/g, '_');
      pdf.save(`${clientName}_Client_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Download all job photos as ZIP
  const handleDownloadPhotosZip = async () => {
    const jobsWithPhotos = clientJobs.filter(j => j.photos && j.photos.length > 0);
    if (jobsWithPhotos.length === 0) {
      alert('No photos found for this client.');
      return;
    }

    setDownloadingPhotos(true);
    try {
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();

      for (const job of jobsWithPhotos) {
        const jobDate = job.date ? format(parseISO(job.date), 'yyyy-MM-dd') : job.id;
        const serviceType = (job.serviceType || 'cleaning').replace(/_/g, '-');
        const folderName = `${jobDate}_${serviceType}`;
        const folder = zip.folder(folderName);

        for (let i = 0; i < job.photos.length; i++) {
          const photo = job.photos[i];
          try {
            let blob;
            if (photo.path) {
              // Use Firebase Storage SDK to avoid CORS issues
              const photoRef = storageRef(storage, photo.path);
              blob = await getBlob(photoRef);
            } else {
              const photoUrl = photo.url || photo;
              const response = await fetch(photoUrl);
              if (!response.ok) continue;
              blob = await response.blob();
            }
            const ext = photo.filename?.split('.').pop() || 'jpg';
            const photoType = photo.type || 'photo';
            const fileName = `${photoType}_${i + 1}.${ext}`;
            folder.file(fileName, blob);
          } catch (err) {
            console.warn(`Failed to fetch photo for zip:`, err);
          }
        }
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const clientName = formatFullName(client).replace(/\s+/g, '_');
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientName}_Photos_${format(new Date(), 'yyyy-MM-dd')}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Photo zip download error:', err);
      alert('Failed to download photos. Please try again.');
    } finally {
      setDownloadingPhotos(false);
    }
  };

  return (
    <div className="detail-page client-detail-expanded">
      <div className="detail-header">
        <Link to="/admin/clients" className="back-link">
          <ArrowLeft size={18} />
          Back to Clients
        </Link>
        
        <div className="detail-header-content">
          <div className="detail-title-section">
            <h1>{formatFullName(client)}</h1>
            <div className="detail-badges">
              <span className={`status-badge ${client.status}`}>{client.status}</span>
              <span className="type-badge">{typeConfig?.label}</span>
              {client.termsViewed ? (
                <span className="status-badge active" title={`Viewed: ${client.termsViewedAt ? format(new Date(client.termsViewedAt), 'MMM d, yyyy') : 'Unknown'}`}>T&C Viewed</span>
              ) : (
                <span className="status-badge inactive" title="Client has not viewed Terms & Conditions">T&C Not Viewed</span>
              )}
            </div>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowWelcomeModal(true)}
              title={client.welcomeEmailSent ? `Last sent: ${client.welcomeEmailSentAt ? format(new Date(client.welcomeEmailSentAt), 'MMM d, yyyy') : 'Unknown'}` : 'Send welcome email with portal instructions'}
            >
              <Mail size={18} />
              {client.welcomeEmailSent ? 'Resend Welcome' : 'Send Welcome Email'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowPinResetModal(true)}
              title={client.pinResetSentAt ? `Last sent: ${format(new Date(client.pinResetSentAt), 'MMM d, yyyy')}` : 'Send PIN reset link to client'}
            >
              <Lock size={18} />
              Send PIN Reset
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadPdf}
              disabled={generatingPdf}
              title="Download client report as PDF"
            >
              {generatingPdf ? <Loader size={18} className="spin" /> : <Download size={18} />}
              {generatingPdf ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadPhotosZip}
              disabled={downloadingPhotos}
              title="Download all job photos as ZIP"
            >
              {downloadingPhotos ? <Loader size={18} className="spin" /> : <Image size={18} />}
              {downloadingPhotos ? 'Downloading...' : 'Download Photos'}
            </button>
            <Link to={`/admin/estimates/new?clientId=${client.id}`} className="btn btn-primary">
              + New Estimate
            </Link>
            <button
              className="btn btn-danger"
              onClick={handleDeleteClientClick}
              title="Delete this client (requires master password)"
            >
              <Trash2 size={18} />
              Delete Client
            </button>
          </div>
        </div>
      </div>

      {/* Account Health Score */}
      <div className="health-score-banner" style={{ borderLeftColor: getHealthScoreColor(accountHealth.healthScore) }}>
        <div className="health-score-circle" style={{ background: getHealthScoreColor(accountHealth.healthScore) }}>
          <span className="score-value">{accountHealth.healthScore}</span>
        </div>
        <div className="health-score-info">
          <h3>Account Health Score</h3>
          <p>Based on profitability, payment history, and service consistency</p>
        </div>
        <div className="health-quick-stats">
          <div className="quick-stat">
            <span className="stat-value">${accountHealth.totalRevenue.toLocaleString()}</span>
            <span className="stat-label">Lifetime Revenue</span>
          </div>
          <div className="quick-stat">
            <span className="stat-value">{accountHealth.completedJobs}</span>
            <span className="stat-label">Jobs Completed</span>
          </div>
          {accountHealth.avgLaborPercent != null && (
            <div className="quick-stat">
              <span className="stat-value" style={{ color: accountHealth.avgLaborPercent > 40 ? '#dc2626' : '#16a34a' }}>
                {accountHealth.avgLaborPercent}%
              </span>
              <span className="stat-label">Avg Labor %</span>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="detail-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          Jobs ({clientJobs.length})
        </button>
        <button 
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices ({clientInvoices.length})
        </button>
        <button 
          className={`tab ${activeTab === 'estimates' ? 'active' : ''}`}
          onClick={() => setActiveTab('estimates')}
        >
          Estimates ({activeEstimates.length})
        </button>
        <button
          className={`tab ${activeTab === 'communications' ? 'active' : ''}`}
          onClick={() => setActiveTab('communications')}
        >
          Communications ({(client?.communicationLog?.length || 0) + (state.communicationLogs || []).filter(l => l.clientId === client?.id).length})
        </button>
        <button
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Documents ({getClientDocuments(id).length})
        </button>
        <button
          className={`tab ${activeTab === 'checklist' ? 'active' : ''}`}
          onClick={() => setActiveTab('checklist')}
        >
          Checklist ({client?.customChecklist?.items?.length || 0})
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="detail-grid">
          {/* Contact Info */}
          <div className="detail-card">
            <h3>Contact Information</h3>
            <div className="info-list">
              <div className="info-item">
                <Phone size={18} />
                <div>
                  <span className="info-label">Phone</span>
                  <span className="info-value">{formatPhone(client.phone)}</span>
                </div>
              </div>
              {client.email && (
                <div className="info-item">
                  <Mail size={18} />
                  <div>
                    <span className="info-label">Email</span>
                    <span className="info-value">{client.email}</span>
                  </div>
                </div>
              )}
              <div className="info-item">
                <Calendar size={18} />
                <div>
                  <span className="info-label">Service Frequency</span>
                  <span className="info-value">{frequency?.label || 'Not set'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="detail-card">
            <h3><DollarSign size={18} /> Financial Summary</h3>
            <div className="financial-grid">
              <div className="fin-stat">
                <span className="fin-label">Total Revenue</span>
                <span className="fin-value">${accountHealth.totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="fin-stat">
                <span className="fin-label">Labor Cost</span>
                <span className="fin-value labor">
                  ${accountHealth.totalLaborCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  {accountHealth.overallLaborPercent != null && (
                    <small className={accountHealth.overallLaborPercent > 40 ? 'over' : 'good'}> ({accountHealth.overallLaborPercent}%)</small>
                  )}
                </span>
              </div>
              <div className="fin-stat highlight">
                <span className="fin-label">Net Profit</span>
                <span className={`fin-value ${accountHealth.totalProfit >= 0 ? 'positive' : 'negative'}`}>
                  ${accountHealth.totalProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              <div className="fin-stat">
                <span className="fin-label">Outstanding</span>
                <span className={`fin-value ${accountHealth.totalOutstanding > 0 ? 'warning' : ''}`}>
                  ${accountHealth.totalOutstanding.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
            </div>
            {accountHealth.avgDaysToPay != null && (
              <div className="payment-speed">
                <Clock size={14} />
                <span>Avg. {accountHealth.avgDaysToPay} days to pay</span>
              </div>
            )}
          </div>

          {/* Job Statistics */}
          <div className="detail-card">
            <h3><CheckCircle size={18} /> Job Statistics</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{accountHealth.totalJobs}</span>
                <span className="stat-desc">Total Jobs</span>
              </div>
              <div className="stat-item">
                <span className="stat-number green">{accountHealth.completedJobs}</span>
                <span className="stat-desc">Completed</span>
              </div>
              <div className="stat-item">
                <span className="stat-number blue">{accountHealth.scheduledJobs}</span>
                <span className="stat-desc">Scheduled</span>
              </div>
              <div className="stat-item">
                <span className="stat-number red">{accountHealth.cancelledJobs}</span>
                <span className="stat-desc">Cancelled</span>
              </div>
            </div>
            {accountHealth.avgActualHours && (
              <div className="time-comparison">
                <span>Avg Time: <strong>{accountHealth.avgActualHours}h actual</strong> vs {accountHealth.avgExpectedHours}h expected</span>
              </div>
            )}
          </div>

          {/* Documentation */}
          <div className="detail-card">
            <h3><Camera size={18} /> Documentation</h3>
            <div className="doc-stats">
              <div className="doc-stat">
                <Camera size={24} />
                <span className="doc-value">{accountHealth.totalPhotos}</span>
                <span className="doc-label">Photos</span>
              </div>
              <div className="doc-stat">
                <AlertTriangle size={24} color={accountHealth.damageReports > 0 ? '#dc2626' : '#9ca3af'} />
                <span className="doc-value">{accountHealth.damageReports}</span>
                <span className="doc-label">Damage Reports</span>
              </div>
            </div>
          </div>

          {/* Properties */}
          <div className="detail-card full-width">
            <div className="section-header-row">
              <h3><Home size={18} /> Properties ({properties.length})</h3>
              {properties.length >= 2 && (
                <button
                  className="merge-properties-btn"
                  onClick={() => {
                    setShowMergePropertyModal(true);
                    setMergePropertyPrimary(properties[0]?.id || null);
                    setMergePropertySecondary(properties[1]?.id || null);
                  }}
                >
                  <GitMerge size={14} /> Merge Duplicates
                </button>
              )}
            </div>
            {properties.length === 0 ? (
              <div className="empty-message">No properties added yet</div>
            ) : (
              <>
              <div className="properties-list">
                {properties.map(prop => (
                  <div key={prop.id} className={`property-item ${editingPropertyId === prop.id ? 'editing' : ''}`}>
                    <div className="property-photo-section">
                      {prop.photoUrl ? (
                        <div className="property-photo">
                          <img src={prop.photoUrl} alt="Property" />
                          <button
                            className="photo-change-btn"
                            onClick={() => {
                              photoInputRef.current.dataset.propertyId = prop.id;
                              photoInputRef.current.click();
                            }}
                            disabled={uploadingPhoto === prop.id}
                          >
                            {uploadingPhoto === prop.id ? <Loader size={14} className="spin" /> : <Camera size={14} />}
                          </button>
                          <button
                            className="photo-delete-btn"
                            onClick={() => handlePropertyPhotoDelete(prop.id, prop.photoUrl)}
                            disabled={deletingPhoto === prop.id}
                            title="Delete photo"
                          >
                            {deletingPhoto === prop.id ? <Loader size={14} className="spin" /> : <Trash2 size={14} />}
                          </button>
                        </div>
                      ) : (
                        <button 
                          className="property-photo-upload"
                          onClick={() => {
                            photoInputRef.current.dataset.propertyId = prop.id;
                            photoInputRef.current.click();
                          }}
                          disabled={uploadingPhoto === prop.id}
                        >
                          {uploadingPhoto === prop.id ? (
                            <><Loader size={18} className="spin" /><span>Uploading...</span></>
                          ) : (
                            <><Camera size={18} /><span>Add Photo</span></>
                          )}
                        </button>
                      )}
                    </div>

                    {editingPropertyId === prop.id ? (
                      /* EDIT MODE */
                      <div className="property-edit-form">
                        <div className="edit-field">
                          <label>Address</label>
                          <input
                            type="text"
                            value={editPropertyData.addressLine1}
                            onChange={e => setEditPropertyData(prev => ({ ...prev, addressLine1: e.target.value }))}
                            placeholder="Street address"
                          />
                        </div>
                        <div className="edit-row-3">
                          <div className="edit-field">
                            <label>City</label>
                            <input
                              type="text"
                              value={editPropertyData.city}
                              onChange={e => setEditPropertyData(prev => ({ ...prev, city: e.target.value }))}
                            />
                          </div>
                          <div className="edit-field">
                            <label>State</label>
                            <input
                              type="text"
                              value={editPropertyData.state}
                              onChange={e => setEditPropertyData(prev => ({ ...prev, state: e.target.value }))}
                              maxLength={2}
                            />
                          </div>
                          <div className="edit-field">
                            <label>ZIP</label>
                            <input
                              type="text"
                              value={editPropertyData.zip}
                              onChange={e => setEditPropertyData(prev => ({ ...prev, zip: e.target.value }))}
                              maxLength={10}
                            />
                          </div>
                        </div>
                        <div className="edit-row-3">
                          <div className="edit-field">
                            <label>Sqft</label>
                            <input
                              type="number"
                              value={editPropertyData.sqft}
                              onChange={e => setEditPropertyData(prev => ({ ...prev, sqft: e.target.value }))}
                            />
                          </div>
                          <div className="edit-field">
                            <label>Beds</label>
                            <input
                              type="number"
                              value={editPropertyData.bedrooms}
                              onChange={e => setEditPropertyData(prev => ({ ...prev, bedrooms: e.target.value }))}
                            />
                          </div>
                          <div className="edit-field">
                            <label>Baths</label>
                            <input
                              type="number"
                              value={editPropertyData.bathrooms}
                              onChange={e => setEditPropertyData(prev => ({ ...prev, bathrooms: e.target.value }))}
                              step="0.5"
                            />
                          </div>
                        </div>
                        <div className="edit-field">
                          <label>Pets</label>
                          <input
                            type="text"
                            value={editPropertyData.pets}
                            onChange={e => setEditPropertyData(prev => ({ ...prev, pets: e.target.value }))}
                            placeholder="e.g. Dog (Max)"
                          />
                        </div>
                        <div className="edit-field">
                          <label>Access Info</label>
                          <textarea
                            value={editPropertyData.accessInfo}
                            onChange={e => setEditPropertyData(prev => ({ ...prev, accessInfo: e.target.value }))}
                            placeholder="Gate codes, lockbox, special instructions..."
                            rows={2}
                          />
                        </div>
                        <div className="edit-actions">
                          <button className="save-btn" onClick={savePropertyEdit}>
                            <Save size={14} /> Save
                          </button>
                          <button className="cancel-btn" onClick={() => { setEditingPropertyId(null); setEditPropertyData({}); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* VIEW MODE */
                      <div className="property-info">
                        <div className="property-address">{formatAddress(prop)}</div>
                        <div className="property-details">
                          {prop.sqft && <span>{prop.sqft.toLocaleString()} sqft</span>}
                          {prop.bedrooms && <span>{prop.bedrooms} bed</span>}
                          {prop.bathrooms && <span>{prop.bathrooms} bath</span>}
                          {prop.pets && <span>🐾 {prop.pets}</span>}
                        </div>
                        {prop.accessInfo && (
                          <div className="property-access-info">🔑 {prop.accessInfo}</div>
                        )}
                      </div>
                    )}

                    <div className="property-actions">
                      {prop.isPrimary && <span className="primary-badge">Primary</span>}
                      {editingPropertyId !== prop.id && (
                        <button
                          className="property-edit-btn"
                          onClick={() => startEditingProperty(prop)}
                          title="Edit address"
                        >
                          <Edit3 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Hidden file input for property photos */}
              <input 
                ref={photoInputRef}
                type="file" 
                accept="image/*" 
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  const propId = e.target.dataset.propertyId;
                  if (file && propId) {
                    handlePropertyPhotoUpload(propId, file);
                  }
                  e.target.value = '';
                }}
              />
              </>
            )}
          </div>

          {/* Notes */}
          {client.notes && (
            <div className="detail-card full-width">
              <h3>Notes</h3>
              <p className="notes-text">{client.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <div className="jobs-list-container">
          {/* Recurring Series Management */}
          {jobSeries.length > 0 && (
            <div className="series-management-section">
              <h4 className="series-section-title">🔄 Recurring Series</h4>
              <div className="series-cards">
                {jobSeries.map(series => (
                  <div key={series.recurringScheduleId} className="series-card">
                    <div className="series-info">
                      <div className="series-name">{series.serviceType} — {series.address || 'No address'}</div>
                      <div className="series-stats">
                        <span className="series-stat scheduled">{series.scheduledCount} scheduled</span>
                        <span className="series-stat completed">{series.completedCount} completed</span>
                        {series.cancelledCount > 0 && <span className="series-stat cancelled">{series.cancelledCount} cancelled</span>}
                        <span className="series-stat total">{series.jobs.length} total</span>
                      </div>
                    </div>
                    {series.scheduledCount > 0 && (
                      <button
                        className="delete-series-btn"
                        onClick={() => setShowDeleteSeriesModal({
                          recurringScheduleId: series.recurringScheduleId,
                          seriesName: `${series.serviceType} — ${series.address || 'this client'}`,
                          scheduledCount: series.scheduledCount,
                          totalCount: series.jobs.length,
                          completedCount: series.completedCount
                        })}
                      >
                        <Trash2 size={14} /> Cancel All
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KPI Summary for this client */}
          {accountHealth.completedJobs > 0 && (
            <div className="client-job-kpis">
              <div className="job-kpi">
                <span className="job-kpi-value">{accountHealth.completedJobs}</span>
                <span className="job-kpi-label">Completed</span>
              </div>
              <div className="job-kpi">
                <span className="job-kpi-value">${accountHealth.totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</span>
                <span className="job-kpi-label">Revenue</span>
              </div>
              <div className="job-kpi">
                <span className={`job-kpi-value ${accountHealth.overallLaborPercent > 40 ? 'over' : ''}`}>
                  {accountHealth.overallLaborPercent != null ? `${accountHealth.overallLaborPercent}%` : '—'}
                </span>
                <span className="job-kpi-label">Labor %</span>
              </div>
              <div className="job-kpi">
                <span className="job-kpi-value">{accountHealth.avgDurationMin > 0 ? `${accountHealth.avgDurationMin}m` : '—'}</span>
                <span className="job-kpi-label">Avg Duration</span>
              </div>
            </div>
          )}

          {/* Bulk Delete Button */}
          {deletableJobs.length > 0 && (
            <div className="bulk-delete-bar">
              <button className="bulk-delete-btn" onClick={() => setShowBulkDeleteModal(true)}>
                <Trash2 size={14} /> Delete All ({deletableJobs.length} scheduled/cancelled)
              </button>
              <span className="bulk-delete-note">Completed jobs are preserved</span>
            </div>
          )}

          {clientJobs.length === 0 ? (
            <div className="empty-state">
              <Calendar size={48} />
              <h3>No jobs yet</h3>
              <p>Jobs will appear here once scheduled</p>
            </div>
          ) : (
            <div className="client-job-log">
              {clientJobs.map(job => {
                const statusConfig = getJobStatusConfig(job.status);
                const isExpanded = expandedJobId === job.id;
                const photos = job.photos || [];
                const team = job.teamNames || job.team || [];
                const teamStr = Array.isArray(team) 
                  ? team.map(t => {
                      if (typeof t === 'string' && t.startsWith('emp')) {
                        const emp = state.employees.find(e => e.id === t);
                        return emp ? `${emp.firstName} ${emp.lastName?.[0] || ''}`.trim() : t;
                      }
                      return t;
                    }).join(', ')
                  : '';
                const helpers = job.helperNames || [];
                const helperStr = helpers.length > 0 ? helpers.join(', ') : '';
                const durationMin = job.actual_duration_min || 0;
                const hrs = Math.floor(durationMin / 60);
                const mins = durationMin % 60;
                const durationStr = durationMin > 0 ? (hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`) : '';

                // Calculate labor same as Reports
                const actualHours = job.actual_duration_min 
                  ? job.actual_duration_min / 60 
                  : (job.actualDuration ? job.actualDuration / 60 : job.duration || 0);
                let calcLabor = 0;
                const allWorkers = [...(job.team || []), ...(job.helpers || [])];
                if (allWorkers.length > 0) {
                  allWorkers.forEach(empId => {
                    const emp = state.employees.find(e => e.id === empId);
                    calcLabor += actualHours * (emp?.hourlyRate || 15);
                  });
                } else if (actualHours > 0) {
                  calcLabor = actualHours * 15 * 2;
                }
                const revenue = job.price || 0;
                const laborPct = revenue > 0 ? Math.round((calcLabor / revenue) * 100) : 0;
                const profit = revenue - calcLabor;

                // Payment status for this job
                const jobInvoice = getJobInvoice(job.id);
                let paymentStatus = 'unbilled';
                let paymentStatusConfig = { label: 'Unbilled', color: '#6b7280', bg: '#f3f4f6' };
                if (jobInvoice) {
                  if (jobInvoice.status === 'paid') {
                    paymentStatus = 'paid';
                    paymentStatusConfig = { label: 'Paid', color: '#16a34a', bg: '#f0fdf4' };
                  } else if (jobInvoice.status === 'sent' || jobInvoice.status === 'pending') {
                    // Check if overdue
                    const dueDate = jobInvoice.dueDate ? new Date(jobInvoice.dueDate) : null;
                    if (dueDate && dueDate < new Date()) {
                      paymentStatus = 'pastdue';
                      paymentStatusConfig = { label: 'Past Due', color: '#dc2626', bg: '#fef2f2' };
                    } else {
                      paymentStatus = 'billed';
                      paymentStatusConfig = { label: 'Billed', color: '#3b82f6', bg: '#eff6ff' };
                    }
                  } else if (jobInvoice.status === 'draft') {
                    paymentStatus = 'draft';
                    paymentStatusConfig = { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' };
                  }
                }

                return (
                  <div key={job.id} className={`jl-card ${isExpanded ? 'expanded' : ''} ${job.status}`}>
                    <div className="jl-header" onClick={() => setExpandedJobId(isExpanded ? null : job.id)}>
                      <div className="jl-left">
                        <div className="jl-date-status">
                          <span className="jl-date">{safeFmt(job.date, 'EEE, MMM d, yyyy')}</span>
                          <span className="status-pill" style={{ background: statusConfig.bg, color: statusConfig.color }}>
                            {statusConfig.label}
                          </span>
                          {job.status === 'completed' && (
                            <span className={`payment-status-badge ${paymentStatus}`} style={{ background: paymentStatusConfig.bg, color: paymentStatusConfig.color }}>
                              {paymentStatusConfig.label}
                            </span>
                          )}
                          {job.recurringScheduleId && <span className="jl-recurring-badge">🔄</span>}
                        </div>
                        <div className="jl-meta">
                          {job.address && <span className="jl-address"><MapPin size={11} /> {job.address}</span>}
                          {teamStr && <span className="jl-team"><Users size={11} /> {teamStr}</span>}
                          {helperStr && <span className="jl-helpers">+ {helperStr}</span>}
                        </div>
                      </div>
                      <div className="jl-right">
                        <span className="jl-price">${revenue.toFixed(2)}</span>
                        {durationStr && <span className="jl-duration"><Clock size={12} /> {durationStr}</span>}
                        {photos.length > 0 && <span className="jl-photos-badge"><Camera size={12} /> {photos.length}</span>}
                      </div>
                      <ChevronDown size={16} className={`jl-chevron ${isExpanded ? 'open' : ''}`} />
                    </div>

                    {isExpanded && (
                      <div className="jl-detail">
                        {/* Timeline */}
                        {job.status === 'completed' && (job.enRouteAt || job.actual_start || job.completedAt || job.actual_end) && (
                          <div className="jl-timeline">
                            <h4>Timeline</h4>
                            <div className="jl-timeline-steps">
                              {job.enRouteAt && (
                                <div className="jl-step"><span className="jl-step-icon en-route"><Navigation size={12} /></span><span className="jl-step-label">En Route</span><span className="jl-step-time">{safeFmt(job.enRouteAt, 'h:mm a')}</span></div>
                              )}
                              {job.actual_start && (
                                <div className="jl-step"><span className="jl-step-icon started"><Play size={12} /></span><span className="jl-step-label">Started</span><span className="jl-step-time">{safeFmt(job.actual_start, 'h:mm a')}</span></div>
                              )}
                              {(job.completedAt || job.actual_end) && (
                                <div className="jl-step"><span className="jl-step-icon done"><Flag size={12} /></span><span className="jl-step-label">Completed</span><span className="jl-step-time">{safeFmt(job.completedAt || job.actual_end, 'h:mm a')}</span></div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Financials grid */}
                        <div className="jl-financials-grid">
                          <div className="jl-fin-item"><span className="jl-fin-label">Revenue</span><span className="jl-fin-value">${revenue.toFixed(2)}</span></div>
                          <div className="jl-fin-item"><span className="jl-fin-label">Labor</span><span className={`jl-fin-value ${laborPct > 40 ? 'over' : ''}`}>${calcLabor.toFixed(2)} <small>({laborPct}%)</small></span></div>
                          <div className="jl-fin-item"><span className="jl-fin-label">Profit</span><span className={`jl-fin-value ${profit >= 0 ? 'positive' : 'negative'}`}>${profit.toFixed(2)}</span></div>
                          <div className="jl-fin-item"><span className="jl-fin-label">Duration</span><span className="jl-fin-value">{durationStr || '—'}</span></div>
                          <div className="jl-fin-item"><span className="jl-fin-label">Service</span><span className="jl-fin-value">{job.serviceType?.replace(/_/g, ' ') || '—'}</span></div>
                          <div className="jl-fin-item"><span className="jl-fin-label">Scheduled</span><span className="jl-fin-value">{job.scheduledTime || '—'}</span></div>
                        </div>

                        {/* Photos */}
                        {photos.length > 0 && (
                          <div className="jl-photos-section">
                            <h4><Camera size={14} /> Photos ({photos.length})</h4>
                            <div className="jl-photo-grid">
                              {photos.map((photo, idx) => {
                                const photoKey = photo.id || photo.path || photo.url || idx;
                                const isDeleting = deletingPhotoId === photoKey;
                                return (
                                  <div key={idx} className="jl-photo-thumb">
                                    <img src={photo.url || photo} alt={photo.type || 'Job photo'} />
                                    {photo.type && <span className="jl-photo-type">{photo.type}</span>}
                                    <button
                                      className="jl-photo-delete-btn"
                                      onClick={(e) => handleDeletePhoto(job.id, photo, e)}
                                      disabled={isDeleting}
                                      title="Delete photo"
                                    >
                                      {isDeleting ? <Loader size={10} className="spin" /> : <X size={10} />}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {job.notes && <div className="jl-notes"><strong>Notes:</strong> {job.notes}</div>}

                        {/* Billing Status Section */}
                        {job.status === 'completed' && (
                          <div className="jl-billing-section">
                            <h4><Receipt size={14} /> Billing</h4>
                            <div className="jl-billing-content">
                              <span className={`payment-status-badge large ${paymentStatus}`} style={{ background: paymentStatusConfig.bg, color: paymentStatusConfig.color }}>
                                {paymentStatusConfig.label}
                              </span>
                              {jobInvoice ? (
                                <div className="jl-invoice-info">
                                  <span className="jl-invoice-number">Invoice #{jobInvoice.invoiceNumber}</span>
                                  {jobInvoice.sentAt && <span className="jl-invoice-sent">Sent {safeFmt(jobInvoice.sentAt, 'MMM d')}</span>}
                                  {jobInvoice.paidAt && <span className="jl-invoice-paid">Paid {safeFmt(jobInvoice.paidAt, 'MMM d')}</span>}
                                  <button className="jl-view-invoice" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${jobInvoice.id}`); }}>
                                    View Invoice →
                                  </button>
                                </div>
                              ) : (
                                <span className="jl-no-invoice">No invoice created yet</span>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="jl-action-buttons">
                          <button className="jl-view-dispatch" onClick={() => navigate(`/dispatch?job=${job.id}`)}>
                            View in Dispatch →
                          </button>
                          <div className="jl-edit-delete-actions">
                            <button className="jl-edit-btn" onClick={(e) => { e.stopPropagation(); openEditJob(job); }}>
                              <Edit3 size={13} /> Edit
                            </button>
                            <button className="jl-delete-btn" onClick={(e) => { e.stopPropagation(); setConfirmDeleteJob(job); }}>
                              <Trash2 size={13} /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="invoices-list-container">
          {clientInvoices.length === 0 ? (
            <div className="empty-state">
              <Receipt size={48} />
              <h3>No invoices yet</h3>
              <p>Invoices will appear here once created</p>
            </div>
          ) : (
            <div className="invoices-table">
              <div className="table-header">
                <span>Invoice #</span>
                <span>Date</span>
                <span>Amount</span>
                <span>Tips</span>
                <span>Status</span>
                <span>Paid</span>
              </div>
              {clientInvoices.map(invoice => (
                <div key={invoice.id} className="table-row" onClick={() => navigate(`/invoices/${invoice.id}`)}>
                  <span className="invoice-number">{invoice.invoiceNumber}</span>
                  <span className="invoice-date">
                    {invoice.createdAt && format(parseISO(invoice.createdAt), 'MMM d, yyyy')}
                  </span>
                  <span className="invoice-amount">${(invoice.total || 0).toFixed(2)}</span>
                  <span className="invoice-tips">
                    {invoice.tipAmount > 0 ? `$${invoice.tipAmount.toFixed(2)}` : '—'}
                  </span>
                  <span className="invoice-status">
                    <span className={`status-pill ${invoice.status}`}>{invoice.status}</span>
                  </span>
                  <span className="invoice-paid">
                    {invoice.paidAt ? format(parseISO(invoice.paidAt), 'MMM d') : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Estimates Tab */}
      {activeTab === 'estimates' && (
        <div className="estimates-container">
          <div className="estimates-header">
            {archivedEstimates.length > 0 && (
              <button 
                className={`btn-text ${showArchived ? 'active' : ''}`}
                onClick={() => setShowArchived(!showArchived)}
              >
                <Archive size={14} />
                {showArchived ? 'Show Active' : `Archived (${archivedEstimates.length})`}
              </button>
            )}
          </div>
          
          {displayEstimates.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <h3>{showArchived ? 'No archived estimates' : 'No estimates yet'}</h3>
              <Link to={`/admin/estimates/new?clientId=${client.id}`} className="btn btn-primary">
                Create Estimate
              </Link>
            </div>
          ) : (
            <div className="estimates-list">
              {displayEstimates.map(estimate => (
                <div 
                  key={estimate.id} 
                  className="estimate-item"
                  onClick={() => navigate(`/admin/estimates/${estimate.id}`)}
                >
                  <div className="estimate-main">
                    <div className="estimate-info">
                      <span className="estimate-address">{estimate.propertyAddress}</span>
                      <span className="estimate-date">
                        Created {format(parseISO(estimate.createdAt), 'MMM d, yyyy')}
                        {estimate.source?.includes('website') && <span className="source-badge">Web Form</span>}
                      </span>
                    </div>
                    <div className="estimate-price">
                      ${estimate.recurringTotal || estimate.deepCleanTotal || 0}
                    </div>
                  </div>
                  <div className="estimate-actions">
                    {(estimate.source?.includes('website') || estimate.calculatorInputs) && (
                      <button
                        className="btn-view-lead"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/estimates/${estimate.id}#lead-submission`);
                        }}
                        title="View Lead Submission"
                      >
                        <FileText size={14} />
                      </button>
                    )}
                    <span 
                      className="status-pill"
                      style={{ background: getStatusColor(estimate.status), color: 'white' }}
                    >
                      {estimate.status}
                    </span>
                    {showArchived ? (
                      <button 
                        className="btn-icon-sm"
                        onClick={(e) => handleRestoreEstimate(estimate.id, e)}
                        title="Restore"
                      >
                        ↩
                      </button>
                    ) : estimate.status !== 'converted' && (
                      <button 
                        className="btn-icon-sm"
                        onClick={(e) => handleArchiveEstimate(estimate.id, e)}
                        title="Archive"
                      >
                        <Archive size={14} />
                      </button>
                    )}
                    <ChevronRight size={16} className="chevron" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Communications Tab */}
      {activeTab === 'communications' && (() => {
        const centralLogs = (state.communicationLogs || [])
          .filter(l => l.clientId === client?.id)
          .map(l => ({
            id: l.id,
            type: l.communicationType,
            subject: `${(l.communicationType || '').charAt(0).toUpperCase() + (l.communicationType || '').slice(1)} - logged by ${l.recordedByName}`,
            status: 'logged',
            timestamp: l.createdAt,
            note: l.note,
            recordedBy: l.recordedByName,
            communicationTime: l.communicationTime,
            communicationDate: l.communicationDate,
            source: 'log'
          }));
        const embeddedLogs = (client?.communicationLog || []).map(l => ({ ...l, source: 'embedded' }));
        const allLogs = [...centralLogs, ...embeddedLogs]
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        return (
          <div className="communications-container">
            {allLogs.length === 0 ? (
              <div className="empty-state">
                <MessageSquare size={48} />
                <h3>No communication history</h3>
                <p>Use the "Log Comm" button to record calls, texts, emails, and in-person communications</p>
              </div>
            ) : (
              <div className="communications-list">
                {allLogs.map(log => (
                  <div key={log.id} className={`comm-item ${log.status}`}>
                    <div className="comm-icon">
                      {log.type === 'email' ? <Mail size={18} /> :
                       log.type === 'call' ? <Phone size={18} /> :
                       log.type === 'text' ? <MessageSquare size={18} /> :
                       log.type === 'in_person' ? <Users size={18} /> :
                       log.type === 'sms' ? <Phone size={18} /> :
                       <Mail size={18} />}
                    </div>
                    <div className="comm-content">
                      <div className="comm-header">
                        <span className="comm-subject">{log.subject || log.category}</span>
                        <div className="comm-status-group">
                          {log.status === 'logged' ? (
                            <span className="comm-status logged">Logged</span>
                          ) : (
                            <span className={`comm-status ${log.status}`}>
                              {log.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                            </span>
                          )}
                          {log.emailOpened && (
                            <span className="comm-status opened" title={log.emailOpenedAt ? `Opened ${format(parseISO(log.emailOpenedAt), 'MMM d, yyyy h:mm a')}` : 'Email was opened'}>
                              👁 Opened
                            </span>
                          )}
                        </div>
                      </div>
                      {log.note && (
                        <div className="comm-note">{log.note}</div>
                      )}
                      <div className="comm-meta">
                        <span className="comm-type">
                          {log.type === 'email' ? 'Email' : log.type === 'call' ? 'Call' :
                           log.type === 'text' ? 'Text' : log.type === 'in_person' ? 'In Person' :
                           log.type === 'sms' ? 'SMS' : log.type}
                        </span>
                        {log.recipient && <span className="comm-to">to {log.recipient}</span>}
                        {log.recordedBy && <span className="comm-to">by {log.recordedBy}</span>}
                        <span className="comm-time">
                          {log.communicationDate
                            ? `${format(parseISO(log.communicationDate), 'MMM d, yyyy')} ${log.communicationTime || ''}`
                            : log.timestamp ? format(parseISO(log.timestamp), 'MMM d, yyyy h:mm a') : ''}
                        </span>
                      </div>
                      {log.error && (
                        <div className="comm-error">
                          <AlertTriangle size={12} /> {log.error}
                        </div>
                      )}
                      {log.details && (
                        <div className="comm-details">
                          {log.details.estimateId && <span>Estimate #{log.details.estimateId.slice(-6)}</span>}
                          {log.details.estimateTotal && <span>${log.details.estimateTotal}</span>}
                          {log.details.invoiceNumber && <span>Invoice {log.details.invoiceNumber}</span>}
                        </div>
                      )}
                      {log.emailOpened && log.emailOpenedAt && (
                        <div className="comm-opened-at">
                          Opened {format(parseISO(log.emailOpenedAt), 'MMM d, yyyy \'at\' h:mm a')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <ClientDocuments client={client} />
      )}

      {/* Checklist Tab */}
      {activeTab === 'checklist' && (
        <div className="detail-card">
          <h3><ClipboardList size={18} /> Custom Cleaning Checklist</h3>
          <p className="checklist-intro">
            Each task can repeat every visit, weekly, bi-weekly, or monthly. Weekly,
            bi-weekly, and monthly tasks show up on the first scheduled visit of the
            period — so a client cleaned three times a week gets weekly tasks on their
            first visit of the week, not all three.
          </p>

          <div className="checklist-editor">
            {(client?.customChecklist?.items || []).map((item, idx) => (
              <div key={item.id} className="checklist-edit-row">
                <span className="checklist-num">{idx + 1}.</span>
                <input
                  type="text"
                  className="checklist-edit-input"
                  value={item.text}
                  disabled={savingChecklist}
                  onChange={async (e) => {
                    const items = [...(client.customChecklist?.items || [])];
                    items[idx] = { ...items[idx], text: e.target.value };
                    await updateClient(client.id, { customChecklist: { items } });
                  }}
                />
                <select
                  className="checklist-freq-select"
                  value={item.frequency || 'daily'}
                  disabled={savingChecklist}
                  onChange={async (e) => {
                    setSavingChecklist(true);
                    const items = [...(client.customChecklist?.items || [])];
                    items[idx] = { ...items[idx], frequency: e.target.value };
                    await updateClient(client.id, { customChecklist: { items } });
                    setSavingChecklist(false);
                  }}
                >
                  {CHECKLIST_FREQUENCY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  className="icon-btn danger"
                  title="Remove item"
                  disabled={savingChecklist}
                  onClick={async () => {
                    setSavingChecklist(true);
                    const items = (client.customChecklist?.items || []).filter(i => i.id !== item.id);
                    await updateClient(client.id, { customChecklist: { items } });
                    setSavingChecklist(false);
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}

            <form
              className="checklist-add-row"
              onSubmit={async (e) => {
                e.preventDefault();
                const text = newChecklistItem.trim();
                if (!text) return;
                setSavingChecklist(true);
                const items = [...(client.customChecklist?.items || [])];
                items.push({
                  id: `cli_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                  text,
                  frequency: newChecklistFrequency,
                });
                await updateClient(client.id, { customChecklist: { items } });
                setNewChecklistItem('');
                setNewChecklistFrequency('daily');
                setSavingChecklist(false);
              }}
            >
              <input
                type="text"
                className="checklist-edit-input"
                placeholder="Add a new task (e.g., Wipe down baseboards in master bath)"
                value={newChecklistItem}
                onChange={(e) => setNewChecklistItem(e.target.value)}
                disabled={savingChecklist}
              />
              <select
                className="checklist-freq-select"
                value={newChecklistFrequency}
                onChange={(e) => setNewChecklistFrequency(e.target.value)}
                disabled={savingChecklist}
              >
                {CHECKLIST_FREQUENCY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                type="submit"
                className="btn-primary"
                disabled={!newChecklistItem.trim() || savingChecklist}
              >
                <Plus size={16} /> Add
              </button>
            </form>

            {(!client?.customChecklist?.items || client.customChecklist.items.length === 0) && (
              <p className="empty-state">
                No custom tasks yet. Add the first one above.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Welcome Email Modal */}
      {showWelcomeModal && (
        <div className="modal-overlay" onClick={() => setShowWelcomeModal(false)}>
          <div className="modal-content welcome-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Welcome Email</h2>
              <button className="close-btn" onClick={() => setShowWelcomeModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="welcome-preview">
                <p className="preview-label">This will send a welcome email to:</p>
                <div className="preview-recipient">
                  <Mail size={20} />
                  <div>
                    <strong>{formatFullName(client)}</strong>
                    <span>{client.email}</span>
                  </div>
                </div>
              </div>

              <div className="welcome-contents">
                <h4>Email includes:</h4>
                <ul>
                  <li><CheckCircle size={14} /> Welcome message</li>
                  <li><CheckCircle size={14} /> Client portal link & login instructions</li>
                  <li><CheckCircle size={14} /> How to view appointments</li>
                  <li><CheckCircle size={14} /> How to message us</li>
                  <li><CheckCircle size={14} /> How to view & pay invoices</li>
                  <li><CheckCircle size={14} /> How to update preferences</li>
                </ul>
              </div>

              {client.welcomeEmailSent && (
                <div className="welcome-warning">
                  <AlertTriangle size={16} />
                  <span>
                    Welcome email was already sent 
                    {client.welcomeEmailSentAt && ` on ${format(new Date(client.welcomeEmailSentAt), 'MMM d, yyyy')}`}
                  </span>
                </div>
              )}

              {!client.email && (
                <div className="welcome-error">
                  <AlertTriangle size={16} />
                  <span>This client doesn't have an email address on file</span>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowWelcomeModal(false)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                disabled={!client.email || sendingWelcome}
                onClick={async () => {
                  setSendingWelcome(true);
                  try {
                    // Get primary property if exists
                    const primaryProperty = properties.find(p => p.isPrimary) || properties[0];
                    const propertyAddress = primaryProperty 
                      ? `${primaryProperty.addressLine1 || primaryProperty.street}, ${primaryProperty.city}, ${primaryProperty.state}`
                      : null;

                    const commLogId = `log_${Date.now()}`;
                    await sendWelcomeEmail({
                      clientId: client.id,
                      commLogId,
                      clientEmail: client.email,
                      clientName: formatFullName(client),
                      portalLink: 'https://keelgroup.app/portal',
                      address: propertyAddress,
                      serviceType: client.frequency ? `${client.frequency.charAt(0).toUpperCase() + client.frequency.slice(1)} Cleaning` : 'Cleaning Service',
                      isExistingClient: client.welcomeEmailSent || false
                    });

                    // Update client record
                    await updateClient(client.id, {
                      welcomeEmailSent: true,
                      welcomeEmailSentAt: new Date().toISOString()
                    });

                    // Log communication
                    await logClientCommunication(client.id, {
                      id: commLogId,
                      type: 'email',
                      category: 'welcome',
                      subject: 'Welcome Email - Portal Instructions',
                      recipient: client.email,
                      status: 'sent'
                    });

                    alert('Welcome email sent successfully!');
                    setShowWelcomeModal(false);
                  } catch (error) {
                    console.error('Failed to send welcome email:', error);
                    alert('Failed to send welcome email. Please try again.');
                  } finally {
                    setSendingWelcome(false);
                  }
                }}
              >
                {sendingWelcome ? (
                  <>
                    <Loader size={18} className="spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Welcome Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN Reset Email Modal */}
      {showPinResetModal && (
        <div className="modal-overlay" onClick={() => setShowPinResetModal(false)}>
          <div className="modal-content welcome-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send PIN Reset Link</h2>
              <button className="close-btn" onClick={() => setShowPinResetModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="welcome-preview">
                <p className="preview-label">This will send a PIN reset link to:</p>
                <div className="preview-recipient">
                  <Mail size={20} />
                  <div>
                    <strong>{formatFullName(client)}</strong>
                    <span>{client.email}</span>
                  </div>
                </div>
              </div>

              <div className="welcome-contents">
                <h4>What happens:</h4>
                <ul>
                  <li><CheckCircle size={14} /> Client receives an email with a secure reset link</li>
                  <li><CheckCircle size={14} /> Clicking the link takes them directly to set a new 4-digit PIN</li>
                  <li><CheckCircle size={14} /> The link expires after 24 hours</li>
                  <li><CheckCircle size={14} /> Their current PIN remains active until they set a new one</li>
                </ul>
              </div>

              {client.pinResetSentAt && (
                <div className="welcome-warning">
                  <AlertTriangle size={16} />
                  <span>
                    A PIN reset was already sent on {format(new Date(client.pinResetSentAt), 'MMM d, yyyy')}
                  </span>
                </div>
              )}

              {!client.email && (
                <div className="welcome-error">
                  <AlertTriangle size={16} />
                  <span>This client doesn't have an email address on file</span>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowPinResetModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!client.email || sendingPinReset}
                onClick={async () => {
                  setSendingPinReset(true);
                  try {
                    // Generate a secure reset token
                    const tokenBytes = crypto.getRandomValues(new Uint8Array(24));
                    const resetToken = Array.from(tokenBytes, b => b.toString(16).padStart(2, '0')).join('');
                    const tokenExpires = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // 24 hours

                    const resetLink = `https://keelgroup.app/portal?resetToken=${resetToken}`;
                    const commLogId = `log_${Date.now()}`;

                    // Save token to client record
                    await updateClient(client.id, {
                      pinResetToken: resetToken,
                      pinResetTokenExpires: tokenExpires,
                      pinResetSentAt: new Date().toISOString()
                    });

                    // Send the email
                    await sendPinResetEmail({
                      clientId: client.id,
                      commLogId,
                      clientEmail: client.email,
                      clientName: formatFullName(client),
                      resetLink
                    });

                    // Log communication
                    await logClientCommunication(client.id, {
                      id: commLogId,
                      type: 'email',
                      category: 'pin_reset',
                      subject: 'PIN Reset Link',
                      recipient: client.email,
                      status: 'sent'
                    });

                    alert('PIN reset email sent successfully!');
                    setShowPinResetModal(false);
                  } catch (error) {
                    console.error('Failed to send PIN reset email:', error);
                    alert('Failed to send PIN reset email. Please try again.');
                  } finally {
                    setSendingPinReset(false);
                  }
                }}
              >
                {sendingPinReset ? (
                  <>
                    <Loader size={18} className="spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send PIN Reset Link
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE SERIES MODAL ===== */}
      {showDeleteSeriesModal && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !deletingSeries && setShowDeleteSeriesModal(null)}>
          <div className="cd-modal delete-series-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h3><Trash2 size={18} /> Delete Series</h3>
              <button className="close-btn" onClick={() => !deletingSeries && setShowDeleteSeriesModal(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="cd-modal-body">
              <div className="series-delete-warning">
                <AlertTriangle size={24} color="#dc2626" />
                <p>You are about to delete jobs from the series:<br /><strong>{showDeleteSeriesModal.seriesName}</strong></p>
              </div>

              <div className="series-delete-options">
                <label className={`radio-option ${deleteSeriesScope === 'all_scheduled' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="deleteScope"
                    value="all_scheduled"
                    checked={deleteSeriesScope === 'all_scheduled'}
                    onChange={() => setDeleteSeriesScope('all_scheduled')}
                  />
                  <div>
                    <strong>Delete all scheduled jobs</strong>
                    <span>Removes {showDeleteSeriesModal.scheduledCount} scheduled job{showDeleteSeriesModal.scheduledCount !== 1 ? 's' : ''}. Completed jobs are kept.</span>
                  </div>
                </label>
                <label className={`radio-option ${deleteSeriesScope === 'all' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="deleteScope"
                    value="all"
                    checked={deleteSeriesScope === 'all'}
                    onChange={() => setDeleteSeriesScope('all')}
                  />
                  <div>
                    <strong>Delete entire series</strong>
                    <span>Removes all non-completed jobs ({showDeleteSeriesModal.totalCount - showDeleteSeriesModal.completedCount} jobs). {showDeleteSeriesModal.completedCount} completed job{showDeleteSeriesModal.completedCount !== 1 ? 's' : ''} will be kept for records.</span>
                  </div>
                </label>
              </div>

              <div className="cd-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => { setShowDeleteSeriesModal(null); setDeleteSeriesScope('all_scheduled'); }}
                  disabled={deletingSeries}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={handleDeleteSeries}
                  disabled={deletingSeries}
                >
                  {deletingSeries ? (
                    <><Loader size={14} className="spin" /> Deleting...</>
                  ) : (
                    <><Trash2 size={14} /> Delete Jobs</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MERGE PROPERTIES MODAL ===== */}
      {showMergePropertyModal && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !mergingProperties && setShowMergePropertyModal(false)}>
          <div className="cd-modal merge-property-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h3><GitMerge size={18} /> Merge Properties</h3>
              <button className="close-btn" onClick={() => !mergingProperties && setShowMergePropertyModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="cd-modal-body">
              <p className="merge-description">
                Select the <strong>primary</strong> property to keep and the <strong>duplicate</strong> to merge into it. All jobs and estimates from the duplicate will be transferred.
              </p>

              <div className="merge-select-group">
                <label>Keep (Primary)</label>
                <select
                  value={mergePropertyPrimary || ''}
                  onChange={e => setMergePropertyPrimary(e.target.value)}
                >
                  <option value="">Select property...</option>
                  {properties.filter(p => p.id !== mergePropertySecondary).map(p => (
                    <option key={p.id} value={p.id}>{formatAddress(p)}</option>
                  ))}
                </select>
              </div>

              <div className="merge-arrow">↓ merge into ↑</div>

              <div className="merge-select-group">
                <label>Remove (Duplicate)</label>
                <select
                  value={mergePropertySecondary || ''}
                  onChange={e => setMergePropertySecondary(e.target.value)}
                >
                  <option value="">Select property...</option>
                  {properties.filter(p => p.id !== mergePropertyPrimary).map(p => (
                    <option key={p.id} value={p.id}>{formatAddress(p)}</option>
                  ))}
                </select>
              </div>

              {mergePropertyPrimary && mergePropertySecondary && (() => {
                const primary = properties.find(p => p.id === mergePropertyPrimary);
                const secondary = properties.find(p => p.id === mergePropertySecondary);
                const secJobs = state.jobs.filter(j => j.propertyId === mergePropertySecondary);
                const secEstimates = state.estimates.filter(e => e.propertyId === mergePropertySecondary);
                return (
                  <div className="merge-preview">
                    <h4>Merge Preview</h4>
                    <div className="merge-preview-row">
                      <span className="merge-label">Address kept:</span>
                      <span>{formatAddress(primary)}</span>
                    </div>
                    <div className="merge-preview-row">
                      <span className="merge-label">Address removed:</span>
                      <span className="strikethrough">{formatAddress(secondary)}</span>
                    </div>
                    <div className="merge-preview-row">
                      <span className="merge-label">Jobs transferred:</span>
                      <span>{secJobs.length}</span>
                    </div>
                    <div className="merge-preview-row">
                      <span className="merge-label">Estimates transferred:</span>
                      <span>{secEstimates.length}</span>
                    </div>
                  </div>
                );
              })()}

              <div className="cd-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowMergePropertyModal(false)}
                  disabled={mergingProperties}
                >
                  Cancel
                </button>
                <button
                  className="merge-confirm-btn"
                  onClick={handleMergeProperties}
                  disabled={mergingProperties || !mergePropertyPrimary || !mergePropertySecondary}
                >
                  {mergingProperties ? (
                    <><Loader size={14} className="spin" /> Merging...</>
                  ) : (
                    <><GitMerge size={14} /> Merge Properties</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== EDIT JOB MODAL ===== */}
      {editingJob && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !savingJob && setEditingJob(null)}>
          <div className="cd-modal edit-job-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h3><Edit3 size={18} /> Edit Job</h3>
              <button className="close-btn" onClick={() => !savingJob && setEditingJob(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="cd-modal-body">
              <div className="edit-job-form">
                <div className="edit-job-row">
                  <div className="edit-job-field">
                    <label>Date</label>
                    <input
                      type="date"
                      value={editJobData.date || ''}
                      onChange={e => setEditJobData(prev => ({ ...prev, date: e.target.value }))}
                    />
                  </div>
                  <div className="edit-job-field">
                    <label>Time</label>
                    <input
                      type="time"
                      value={editJobData.scheduledTime || ''}
                      onChange={e => setEditJobData(prev => ({ ...prev, scheduledTime: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="edit-job-row">
                  <div className="edit-job-field">
                    <label>Service Type</label>
                    <select
                      value={editJobData.serviceType || ''}
                      onChange={e => setEditJobData(prev => ({ ...prev, serviceType: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="one_time">One-Time</option>
                      <option value="move_in_out">Move In/Move Out</option>
                      <option value="commercial">Commercial</option>
                      <option value="airbnb">Short-Term Rental</option>
                    </select>
                  </div>
                  <div className="edit-job-field">
                    <label>Status</label>
                    <select
                      value={editJobData.status || 'scheduled'}
                      onChange={e => setEditJobData(prev => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="edit-job-row">
                  <div className="edit-job-field">
                    <label>Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editJobData.price || ''}
                      onChange={e => setEditJobData(prev => ({ ...prev, price: e.target.value }))}
                    />
                  </div>
                  <div className="edit-job-field">
                    <label>Est. Duration (hrs)</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={editJobData.duration || ''}
                      onChange={e => setEditJobData(prev => ({ ...prev, duration: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="edit-job-field full-width">
                  <label>Address</label>
                  <input
                    type="text"
                    value={editJobData.address || ''}
                    onChange={e => setEditJobData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Job address"
                  />
                </div>

                <div className="edit-job-field full-width">
                  <label>Notes</label>
                  <textarea
                    value={editJobData.notes || ''}
                    onChange={e => setEditJobData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Job notes..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="cd-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setEditingJob(null)}
                  disabled={savingJob}
                >
                  Cancel
                </button>
                <button
                  className="save-job-btn"
                  onClick={handleSaveJob}
                  disabled={savingJob}
                >
                  {savingJob ? (
                    <><Loader size={14} className="spin" /> Saving...</>
                  ) : (
                    <><Save size={14} /> Save Changes</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE JOB CONFIRM MODAL ===== */}
      {confirmDeleteJob && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !deletingJobId && setConfirmDeleteJob(null)}>
          <div className="cd-modal delete-job-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h3><Trash2 size={18} /> Delete Job</h3>
              <button className="close-btn" onClick={() => !deletingJobId && setConfirmDeleteJob(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="cd-modal-body">
              <div className="series-delete-warning">
                <AlertTriangle size={24} color="#dc2626" />
                <div>
                  <p>Are you sure you want to delete this job?</p>
                  <div className="delete-job-details">
                    <span><strong>{safeFmt(confirmDeleteJob.date, 'EEE, MMM d, yyyy')}</strong></span>
                    <span>{confirmDeleteJob.serviceType?.replace(/_/g, ' ')} — ${(confirmDeleteJob.price || 0).toFixed(2)}</span>
                    {confirmDeleteJob.address && <span>{confirmDeleteJob.address}</span>}
                  </div>
                  <p className="delete-warning-sub">This action cannot be undone.</p>
                </div>
              </div>
              <div className="cd-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setConfirmDeleteJob(null)}
                  disabled={!!deletingJobId}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={() => handleDeleteJob(confirmDeleteJob.id)}
                  disabled={!!deletingJobId}
                >
                  {deletingJobId === confirmDeleteJob.id ? (
                    <><Loader size={14} className="spin" /> Deleting...</>
                  ) : (
                    <><Trash2 size={14} /> Delete Job</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== BULK DELETE JOBS MODAL ===== */}
      {showBulkDeleteModal && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}>
          <div className="cd-modal delete-job-modal" onClick={e => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h3><Trash2 size={18} /> Delete All Non-Completed Jobs</h3>
              <button className="close-btn" onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="cd-modal-body">
              <div className="series-delete-warning">
                <AlertTriangle size={24} color="#dc2626" />
                <div>
                  <p>You are about to delete <strong>{deletableJobs.length} job{deletableJobs.length !== 1 ? 's' : ''}</strong> for this client.</p>
                  <div className="delete-job-details">
                    {deletableJobs.filter(j => j.status === 'scheduled').length > 0 && (
                      <span>📅 {deletableJobs.filter(j => j.status === 'scheduled').length} scheduled</span>
                    )}
                    {deletableJobs.filter(j => j.status === 'cancelled').length > 0 && (
                      <span>❌ {deletableJobs.filter(j => j.status === 'cancelled').length} cancelled</span>
                    )}
                    {deletableJobs.filter(j => j.status === 'in_progress').length > 0 && (
                      <span>⏳ {deletableJobs.filter(j => j.status === 'in_progress').length} in progress</span>
                    )}
                    {clientJobs.filter(j => j.status === 'completed').length > 0 && (
                      <span>✅ {clientJobs.filter(j => j.status === 'completed').length} completed — <strong>will be kept</strong></span>
                    )}
                  </div>
                  <p className="delete-warning-sub">This action cannot be undone.</p>
                </div>
              </div>
              <div className="cd-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={bulkDeleting}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={handleBulkDeleteJobs}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? (
                    <><Loader size={14} className="spin" /> Deleting {deletableJobs.length} jobs...</>
                  ) : (
                    <><Trash2 size={14} /> Delete {deletableJobs.length} Job{deletableJobs.length !== 1 ? 's' : ''}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Client Modal */}
      {showDeleteClientModal && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !deletingClient && (setShowDeleteClientModal(false), setMasterPasswordInput(''), setDeleteClientError(''))}>
          <div className="modal-content" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Trash2 size={18} /> Delete Client</h3>
              <button className="close-btn" onClick={() => { setShowDeleteClientModal(false); setMasterPasswordInput(''); setDeleteClientError(''); }} disabled={deletingClient}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-banner" style={{ display: 'flex', gap: 10, padding: 12, background: '#fef2f2', borderRadius: 8, marginBottom: 16 }}>
                <AlertTriangle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  <strong>This action cannot be undone.</strong> You are about to permanently delete
                  <strong> {formatFullName(client)}</strong>.
                </p>
              </div>

              {deleteClientError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: '0.85rem', marginBottom: 16 }}>
                  <Lock size={16} />
                  {deleteClientError}
                </div>
              )}

              {!deleteClientError?.includes('not been set') && (
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="masterPasswordDetail" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
                    <Lock size={16} />
                    Enter Master Password
                  </label>
                  <input
                    id="masterPasswordDetail"
                    type="password"
                    value={masterPassword}
                    onChange={(e) => setMasterPasswordInput(e.target.value)}
                    placeholder="Enter master password to confirm"
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirmDeleteClient()}
                    autoFocus
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.9rem' }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setShowDeleteClientModal(false); setMasterPasswordInput(''); setDeleteClientError(''); }}
                  disabled={deletingClient}
                >
                  Cancel
                </button>
                {!deleteClientError?.includes('not been set') && (
                  <button
                    className="btn btn-danger"
                    onClick={handleConfirmDeleteClient}
                    disabled={deletingClient || !masterPassword.trim()}
                  >
                    {deletingClient ? (
                      <><RefreshCw size={16} className="spin" /> Deleting...</>
                    ) : (
                      <><Trash2 size={16} /> Delete Client</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDetail;
